<!-- TablePreviewModal.vue -->
<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useVueTable
} from '@tanstack/vue-table';
import { buildPreviewLimit, previewLimitCard } from './previewLimit';

const props = defineProps({
  show: { type: Boolean, required: true },
  tableData: { type: Array, required: true, default: () => [] },
  headerTitle: { type: String, required: false, default: '' },
  isLoading: Boolean,
  totalCount: { type: Number, default: 0 },
  whatsappLimit: { type: Object, default: () => ({}) },
  // Leitura do limite falhou (ver useDataFetching#fetchWhatsappLimit).
  whatsappLimitError: { type: Boolean, default: false },
  whatsappLimitLoading: { type: Boolean, default: false },
  generatedSql: { type: String, default: null }, // ✅ NOVO: SQL gerada
});

const emit = defineEmits(['update:show', 'close']);

const { t } = useI18n();

const isLoading = ref(false);
const showSql = ref(false); // ✅ NOVO: Estado para mostrar/esconder SQL

watch(
  () => props.isLoading,
  (newVal) => {
    if (newVal) {
      isLoading.value = true;
    } else {
      setTimeout(() => {
        isLoading.value = false;
      }, 500);
    }
  },
  { immediate: true }
);

const tableColumns = computed(() => {
  if (!props.tableData.length || !props.tableData[0]) {
    return [];
  }
  const columnHelper = createColumnHelper();
  return Object.keys(props.tableData[0]).map((col) => {
    return columnHelper.accessor(col, {
      id: col,
      header: col,
      cell: (info) => info.getValue(),
      enableSorting: true,
    });
  });
});

const sorting = ref([]);

// ✅ FIX: Remover paginação manual - deixar TanStack Table gerenciar
const table = useVueTable({
  get data() {
    return props.tableData; // ✅ Passa TODOS os dados
  },
  get columns() {
    return tableColumns.value;
  },
  state: {
    get sorting() {
      return sorting.value;
    },
  },
  initialState: {
    pagination: {
      pageIndex: 0,
      pageSize: 10,
    },
  },
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(), // ✅ TanStack faz a paginação
  getSortedRowModel: getSortedRowModel(),
  onSortingChange: updaterOrValue => {
    sorting.value = typeof updaterOrValue === 'function' 
      ? updaterOrValue(sorting.value) 
      : updaterOrValue;
  },
  // ✅ REMOVIDO: manualPagination e manualSorting
  // ✅ REMOVIDO: onStateChange personalizado
});

const metricsCards = computed(() => {
  const totalRecords = props.totalCount || 0;
  const displayedRecords = props.tableData.length;

  // Regra do card de limite em previewLimit.js (testada lá).
  const limitCard = previewLimitCard(
    buildPreviewLimit({
      whatsappLimit: props.whatsappLimit,
      totalCount: totalRecords,
      error: props.whatsappLimitError,
      loading: props.whatsappLimitLoading,
    }),
    t
  );

  return [
    {
      icon: '📊',
      title: 'Registros Encontrados',
      value: totalRecords.toLocaleString('pt-BR'),
      subtitle: `${displayedRecords} exibidos`,
      bgColor: 'bg-woot-100 dark:bg-green-900',
      textColor: 'text-woot-800 dark:text-woot-300',
      iconColor: 'text-woot-600'
    },
    limitCard,
  ];
});

const showModal = computed({
  get: () => props.show,
  set: (value) => emit('update:show', value),
});

const closeModal = () => {
  emit('close');
};

// ✅ Reset quando modal abre
watch(showModal, (newVal) => {
  if (newVal) {
    table.setPageIndex(0);
    table.setSorting([]);
  }
});

// ✅ Reset quando dados mudam
watch(() => props.tableData, () => {
  table.setPageIndex(0);
  table.setSorting([]);
});
</script>

<template>
  <Modal
    v-model:show="showModal"
    :showCloseButton="true"
    :closeOnBackdropClick="true"
    modalType="centered"
    contentPadding="px-2 mb-2 mt-2"
    size="large"
    @close="closeModal"
  >
    <!-- Header -->
    <div class="whatsapp-modal-header">
      <div class="header-background">
        <div class="header-content">
          <div class="header-info">
            <div class="header-icon">
              <span class="table-icon">📊</span>
            </div>
            <div class="header-text">
              <h2 class="header-title">
                {{ props.headerTitle || 'Pré-visualização da Tabela' }}
              </h2>
              <!-- ✅ NOVO: Botão para mostrar/esconder SQL -->
              <button
                v-if="generatedSql"
                @click="showSql = !showSql"
                class="sql-toggle-btn"
                type="button"
              >
                <span class="i-lucide-code text-xs"></span>
                <span class="text-xs">{{ showSql ? 'Ocultar SQL' : 'Ver SQL' }}</span>
                <span :class="showSql ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" class="text-xs"></span>
              </button>
            </div>
          </div>

          <div class="header-stats">
            <div
              v-for="card in metricsCards"
              :key="card.title"
              v-tooltip.top="card.hint"
              class="stat-card"
              :class="card.bgColor"
            >
              <div class="stat-icon" :class="card.iconColor">{{ card.icon }}</div>
              <div class="stat-number" :class="card.textColor">{{ card.value }}</div>
              <div class="stat-label" :class="card.textColor">{{ card.title }}</div>
              <div class="stat-subtitle" :class="card.textColor">{{ card.subtitle }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ✅ NOVO: SQL Panel (collapse) -->
    <div v-if="generatedSql && showSql" class="sql-panel">
      <div class="sql-header">
        <span class="i-lucide-database text-sm"></span>
        <span class="text-sm font-medium">SQL Gerada</span>
        <button @click="navigator.clipboard.writeText(generatedSql)" class="copy-btn" type="button" title="Copiar SQL">
          <span class="i-lucide-copy text-xs"></span>
          <span class="text-xs">Copiar</span>
        </button>
      </div>
      <pre class="sql-code"><code>{{ generatedSql }}</code></pre>
    </div>

    <!-- Content -->
    <div class="modal-content">
      <!-- Loading -->
      <div v-if="isLoading" class="loading-container">
        <div class="loading-card">
          <Spinner />
          <p class="loading-text">Carregando dados da tabela...</p>
        </div>
      </div>

      <!-- Table -->
      <div v-else-if="tableData.length > 0" class="table-preview-container">
        <div class="table-wrapper">
          <Table :table="table" type="fixed" class="preview-table" />
        </div>

        <div class="pagination-wrapper">
          <Pagination
            :table="table"
            :default-page-size="10"
            :show-page-size-selector="true"
            class="whatsapp-pagination"
          />
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-container">
        <div class="empty-state-card">
          <div class="empty-icon">📋</div>
          <h3 class="empty-title">Nenhum dado disponível</h3>
          <p class="empty-description">
            A tabela selecionada não possui dados para visualização ou os filtros aplicados não retornaram resultados.
          </p>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
.modal-content {
  @apply bg-n-background;
  @apply min-h-[500px] overflow-hidden;
  @apply pl-0;
}

.whatsapp-modal-header {
  @apply relative overflow-hidden;
}

.header-background {
  @apply bg-green-900/95;
}


.header-content {
  @apply px-6 py-4 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4;
}

.header-info {
  @apply flex items-center gap-4;
}

.header-icon {
  @apply w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm;
}

.table-icon {
  @apply text-2xl;
}

.header-text {
  @apply flex flex-col gap-1;
}

.header-title {
  @apply text-xl font-bold text-white m-0;
}

/* ✅ NOVO: Botão SQL */
.sql-toggle-btn {
  @apply mt-2 flex items-center gap-1.5 px-2.5 py-1 rounded-md;
  @apply bg-white/10 hover:bg-white/20 text-white/90 hover:text-white;
  @apply border border-white/20 hover:border-white/30;
  @apply transition-all duration-200;
}

/* ✅ NOVO: SQL Panel */
.sql-panel {
  @apply bg-slate-900 border-b border-slate-700 overflow-hidden;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    max-height: 0;
    opacity: 0;
  }
  to {
    max-height: 300px;
    opacity: 1;
  }
}

.sql-header {
  @apply flex items-center gap-2 px-4 py-2 bg-slate-800 border-b border-slate-700 text-slate-300;
}

.copy-btn {
  @apply ml-auto flex items-center gap-1 px-2 py-1 rounded;
  @apply bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white;
  @apply transition-colors duration-200;
}

.sql-code {
  @apply px-4 py-3 text-xs text-green-400 font-mono overflow-x-auto;
  @apply max-h-[200px] overflow-y-auto;
  @apply bg-slate-900;
}

.sql-code::-webkit-scrollbar {
  @apply w-2 h-2;
}

.sql-code::-webkit-scrollbar-track {
  @apply bg-slate-800 rounded;
}

.sql-code::-webkit-scrollbar-thumb {
  @apply bg-slate-600 rounded;
}

.sql-code::-webkit-scrollbar-thumb:hover {
  @apply bg-slate-500;
}

.header-stats {
  @apply flex flex-wrap gap-3;
}

.stat-card {
  @apply backdrop-blur-sm rounded-lg px-4 py-3 text-center min-w-[140px] border border-white/20;
}

.stat-icon {
  @apply text-lg mb-1;
}

.stat-number {
  @apply text-lg font-bold;
}

.stat-label {
  @apply text-xs font-medium opacity-90;
}

.stat-subtitle {
  @apply text-xs opacity-75 mt-1;
}

.loading-container {
  @apply flex justify-center items-center min-h-64 p-8;
}

.loading-card {
  @apply bg-white dark:bg-slate-800 rounded-xl p-8 shadow-lg border border-green-100 dark:border-green-800 text-center;
}

.loading-text {
  @apply mt-4 text-slate-600 dark:text-slate-400;
}

.table-wrapper {
  @apply bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden;
  @apply max-h-[500px] overflow-auto;
}

:deep(.preview-table) {
  @apply w-full;
}

:deep(.preview-table thead tr) {
  @apply bg-green-200 dark:bg-green-900/20;
}

:deep(.preview-table th) {
  @apply font-semibold text-green-800 dark:text-green-300 p-3 text-left border-b border-slate-200 dark:border-slate-800 sticky top-0 z-10;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.preview-table td) {
  @apply p-3 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-300;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.preview-table tbody tr:hover) {
  @apply bg-slate-50 dark:bg-green-900/10;
}

:deep(.preview-table tbody tr:last-child td) {
  @apply border-b-0;
}

.pagination-wrapper {
  @apply border-t border-green-100 dark:border-green-800 p-4 bg-green-50 dark:bg-green-900/10;
}

:deep(.whatsapp-pagination) {
  @apply text-green-700 dark:text-green-300;
}

:deep(.whatsapp-pagination button) {
  @apply text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20;
}

.empty-state-container {
  @apply flex justify-center items-center min-h-64 p-8;
}

.empty-state-card {
  @apply bg-white dark:bg-slate-800 rounded-xl p-8 shadow-lg border border-green-100 dark:border-green-800 text-center max-w-md;
}

.empty-icon {
  @apply text-4xl mb-4;
}

.empty-title {
  @apply text-lg font-semibold text-slate-900 dark:text-white mb-2;
}

.empty-description {
  @apply text-slate-600 dark:text-slate-400 leading-relaxed;
}

.table-wrapper::-webkit-scrollbar {
  @apply w-2 h-2;
}

.table-wrapper::-webkit-scrollbar-track {
  @apply bg-green-100 dark:bg-green-900/20 rounded;
}

.table-wrapper::-webkit-scrollbar-thumb {
  @apply bg-green-300 dark:bg-green-700 rounded;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
  @apply bg-green-400 dark:bg-green-600;
}

@media (max-width: 768px) {
  .header-content {
    @apply flex-col gap-3 text-center;
  }

  .header-stats {
    @apply flex-wrap justify-center gap-2;
  }

  :deep(.preview-table th),
  :deep(.preview-table td) {
    @apply p-2 text-xs;
    max-width: 120px;
  }

  .modal-content {
    @apply max-h-[90vh];
  }

  .table-wrapper {
    @apply max-h-[350px];
  }
}
</style>